import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";

import Form from "../base/Form/Form";
import { Button } from "../base/Button";
import { FormErrors } from "../base/Form/FormErrors";
import { FormTextField } from "../base/Form/FormTextField";
import Modal, { ModalControlProps } from "../../layouts/Modal";
import ToolRunConfirmation from "../Tools/ToolRunConfirmation";
import useCoreDeploymentMutations from "../../data/CoreDeployment/useCoreDeploymentMutations";
import useNotification from "../shared/Notifications/useNotifications";
import { toDateTimeLocalValue } from "./coreDeploymentMeta";

interface ScheduleForm {
  /** datetime-local value in the browser's local time. */
  scheduledFor: string;
  deployCore: boolean;
  deployTme: boolean;
  notes: string;
}

const warningText =
  "This merges core-staging into the selected deployment branches at the scheduled time. " +
  "main must already be merged into core-staging on both Wilma.Core.Api and Wilma.Core.Dashboard. " +
  "The staging deploy is checked at the scheduled time; if either staging workflow has failed, nothing goes out.";

/** Default to the next quarter hour at least 15 minutes away. */
function defaultScheduledFor(): string {
  const next = new Date(Date.now() + 15 * 60 * 1000);
  next.setMinutes(Math.ceil(next.getMinutes() / 15) * 15, 0, 0);
  return toDateTimeLocalValue(next);
}

export function ScheduleCoreDeploymentModal({ open, setOpen }: ModalControlProps) {
  const { schedule } = useCoreDeploymentMutations();
  const { addNotification } = useNotification();
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open]);

  const onSubmit = (data: ScheduleForm) => {
    const scheduledFor = new Date(data.scheduledFor);
    if (isNaN(scheduledFor.getTime())) {
      addNotification({ variant: "error", primaryText: "Enter a valid date and time" });
      return;
    }

    schedule.mutate(
      {
        scheduledFor: scheduledFor.toISOString(),
        deployCore: !!data.deployCore,
        deployTme: !!data.deployTme,
        notes: data.notes?.trim() || undefined,
      },
      {
        onSuccess: () => {
          addNotification({ variant: "success", primaryText: "Deployment scheduled" });
          setOpen(false);
        },
        onError: (error: Error) => {
          addNotification({
            variant: "error",
            primaryText: error?.message || "Failed to schedule deployment",
          });
        },
      },
    );
  };

  return (
    <Modal open={open} setOpen={setOpen} width="!w-1/3">
      <div>
        <div className="border-b">
          <h2 className="text-lg font-stick font-medium leading-6 mb-4 text-gray-900">
            Schedule Core Deployment
          </h2>
        </div>
        <Form<ScheduleForm>
          defaultValues={{
            scheduledFor: defaultScheduledFor(),
            deployCore: true,
            deployTme: false,
            notes: "",
          }}
          onSubmit={onSubmit}
          className="grid grid-cols-1 gap-y-4"
        >
          {({ register, watch, formState: { errors } }: UseFormReturn<ScheduleForm>) => {
            const deployCore = watch("deployCore");
            const deployTme = watch("deployTme");

            return (
              <>
                <div className="-m-4">
                  <FormErrors />
                </div>

                <div>
                  <FormTextField
                    {...register("scheduledFor", { required: true })}
                    type="datetime-local"
                    label="Deploy at (local time)"
                    min={toDateTimeLocalValue(new Date())}
                    fullWidth
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    The worker checks every minute and starts at the first tick on or after this time.
                  </p>
                </div>

                <fieldset>
                  <legend className="text-sm text-black font-medium opacity-80">Targets</legend>
                  <div className="mt-2 flex flex-col gap-2">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                      <input
                        type="checkbox"
                        {...register("deployCore", {
                          validate: (value, values) =>
                            value || values.deployTme || "Select at least one target",
                        })}
                        className="w-4 h-4 rounded border-orange-600 text-orange-600 focus:ring-orange-600"
                      />
                      <span>
                        Core <span className="text-gray-500">(core-staging → core-deployment, all core tenants)</span>
                      </span>
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                      <input
                        type="checkbox"
                        {...register("deployTme", {
                          validate: (value, values) =>
                            value || values.deployCore || "Select at least one target",
                        })}
                        className="w-4 h-4 rounded border-orange-600 text-orange-600 focus:ring-orange-600"
                      />
                      <span>
                        TME <span className="text-gray-500">(core-staging → tme-deployment)</span>
                      </span>
                    </label>
                  </div>
                  {!deployCore && !deployTme && (
                    <p className="text-xs text-red-500 mt-2">Select at least one target</p>
                  )}
                </fieldset>

                <div>
                  <FormTextField {...register("notes", { maxLength: 1000 })} label="Notes (optional)" fullWidth />
                </div>

                <ToolRunConfirmation
                  checked={acknowledged}
                  onCheckedChange={setAcknowledged}
                  warningText={warningText}
                />

                <div className="flex gap-2 mt-2">
                  <Button
                    type="submit"
                    disabled={!acknowledged || schedule.isPending || (!deployCore && !deployTme) || !!errors.scheduledFor}
                  >
                    {schedule.isPending ? "Scheduling..." : "Schedule"}
                  </Button>
                  <Button type="button" variant="outlined" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </>
            );
          }}
        </Form>
      </div>
    </Modal>
  );
}
